// /auth/callback — PKCE-only auth callback (ADR-015, ADR-018).
//
// Flow:
//   1. Page mounts. If `?code=<…>` is present, supabase-js's
//      `detectSessionInUrl: true` is *already* exchanging it via
//      `_initialize → _exchangeCodeForSession` while we render. We additionally
//      observe error states surfaced by GoTrue (which writes them back into
//      the URL hash on failure: `#error=…&error_code=…`).
//   2. If the URL has a hash-fragment `#access_token=…` (legacy implicit
//      flow), we emit a warning audit event but still let supabase-js
//      consume it — preserving recovery for any old in-flight magic link
//      that hasn't expired yet. New email templates render PKCE shape
//      (`?code=`) so this path becomes unreachable for fresh links.
//   3. Once the AuthProvider transitions out of `loading`, route based on
//      status:
//        - authenticated  → /support
//        - unprovisioned  → /not-provisioned
//        - {kind:'error'} → /auth/<reason> (typed recovery page)
//        - unauthenticated → /auth/expired (most common cause of a no-op
//          callback is a stale link — better than dumping the user on
//          /login with no context).

import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useAuth, isAuthError, type AuthErrorReason } from '../lib/auth';
import { writeAuditEvent } from '../lib/audit';
import { logger } from '../lib/logger';
import brandmarkBlack from '../assets/solomon-brandmark-black.svg';

interface CallbackUrlInfo {
  hasCode: boolean;
  hasLegacyHashTokens: boolean;
  errorReason: AuthErrorReason | null;
}

/**
 * Inspects the current URL for callback artefacts. Pure for testability.
 */
export function readCallbackUrl(href: string): CallbackUrlInfo {
  const url = new URL(href);
  const hashParams = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
  const hasCode = url.searchParams.has('code');
  const hasLegacyHashTokens = hashParams.has('access_token') || hashParams.has('refresh_token');

  // GoTrue surfaces failure modes via either query string OR hash fragment.
  // The codes we map to dedicated routes:
  //   - otp_expired               → link_expired
  //   - access_denied (with otp_expired hint above) → link_expired
  //   - invalid_request           → link_invalid
  //   - flow_state_not_found      → link_consumed
  // Anything unrecognised falls through to `unknown`.
  const errorCodeRaw =
    hashParams.get('error_code') ??
    url.searchParams.get('error_code') ??
    hashParams.get('error') ??
    url.searchParams.get('error');
  let errorReason: AuthErrorReason | null = null;
  if (errorCodeRaw) {
    const c = errorCodeRaw.toLowerCase();
    if (c === 'otp_expired' || c.includes('expired')) errorReason = 'link_expired';
    else if (c === 'access_denied') errorReason = 'link_expired';
    else if (c === 'flow_state_not_found' || c === 'flow_state_expired')
      errorReason = 'link_consumed';
    else if (c === 'invalid_request' || c === 'bad_code_verifier' || c.includes('invalid'))
      errorReason = 'link_invalid';
    else errorReason = 'unknown';
  }

  return { hasCode, hasLegacyHashTokens, errorReason };
}

function reasonToRoute(reason: AuthErrorReason): string {
  switch (reason) {
    case 'link_expired':
      return '/auth/expired';
    case 'link_invalid':
      return '/auth/invalid-link';
    case 'link_consumed':
      return '/auth/already-used';
    case 'rls_failure':
    case 'network_timeout':
    case 'pkce_exchange_failed':
    case 'unknown':
    default:
      return '/auth/error';
  }
}

export function AuthCallbackRoute(): React.ReactNode {
  const { status } = useAuth();
  const navigate = useNavigate();

  const initial: CallbackUrlInfo = useMemo(() => {
    if (typeof window === 'undefined') {
      return { hasCode: false, hasLegacyHashTokens: false, errorReason: null };
    }
    return readCallbackUrl(window.location.href);
  }, []);

  // Emit a one-time audit warning if we observe legacy hash tokens — that's
  // a misconfiguration alarm (we expect PKCE shape now). See ADR-015.
  useEffect(() => {
    if (initial.hasLegacyHashTokens) {
      logger.warn({
        source: 'auth-callback',
        message: 'legacy hash-fragment tokens observed (ADR-015 fallback path)',
      });
      void writeAuditEvent({
        event_type: 'auth.warning.legacy_hash_fragment',
        payload: { has_code: initial.hasCode },
      });
    }
    if (initial.errorReason) {
      void writeAuditEvent({
        event_type: `auth.error.${initial.errorReason}`,
        payload: { source: 'auth-callback.url' },
      });
    }
  }, [initial.errorReason, initial.hasCode, initial.hasLegacyHashTokens]);

  // Strip auth artefacts from the URL once we know we're past loading.
  useEffect(() => {
    if (status === 'loading') return;
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const dirty =
      url.searchParams.has('code') ||
      url.searchParams.has('error') ||
      url.searchParams.has('error_code') ||
      url.hash.includes('access_token') ||
      url.hash.includes('error');
    if (dirty) {
      url.hash = '';
      url.search = '';
      window.history.replaceState({}, '', url.pathname);
    }
  }, [status]);

  // Hard-route on URL-level error before checking session status: GoTrue
  // already failed (the SDK won't have a session), and we want users on the
  // typed recovery page, not /login.
  if (initial.errorReason) {
    void navigate(reasonToRoute(initial.errorReason), { replace: true });
    return null;
  }

  if (status === 'loading') {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        role="status"
        aria-live="polite"
        className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cream text-text-muted focus:outline-none"
      >
        <img src={brandmarkBlack} alt="Solomon Advising" className="h-12 w-auto" />
        <p>Signing you in…</p>
      </main>
    );
  }

  if (status === 'authenticated') {
    return <Navigate to="/support" replace />;
  }
  if (status === 'unprovisioned') {
    return <Navigate to="/not-provisioned" replace />;
  }
  if (isAuthError(status)) {
    return <Navigate to={reasonToRoute(status.reason)} replace />;
  }

  // unauthenticated and the URL had no error fragment. Most often: user
  // navigated to /auth/callback directly without a code, or the link was
  // reused. Steer to /auth/expired which has a "send me a new link" path.
  void navigate('/auth/expired', { replace: true });
  return null;
}
